import React, { useState, useEffect, useRef } from "react";
import { FaUser, FaDice, FaMoneyBillWave, FaTrophy, FaHeadset, FaTimes, FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown, FaUserFriends, FaGift, FaCoins, FaPercent, FaApple, FaAndroid, FaTelegram, FaWindows, FaSignInAlt, FaPhone, FaMailBulk, FaFire, FaStar, FaBolt, FaRocket, FaThumbsUp, FaHeart, FaFish, FaDollarSign, FaVideo, FaCrown, FaGem, FaHistory, FaGamepad } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { usePayment } from "../context/PaymentContext";
import { useAuth } from "../context/AuthContext";
import { useGeo } from "../context/GeoContext";
import ProfileModal from "./ProfileModal";
import AccountHistoryModal from "./AccountHistoryModal";
import GameHistoryModal from "./GameHistoryModal";
import AuthModal from "./AuthModal";
import IosInstallModal from "./IosInstallModal";
import "../styles/Sidebar.css";
import { useTranslation } from "react-i18next";
import LangDropdownList from "./LangDropdown";
import "../styles/Modal.css";
import { getIcon } from '../utils/iconHelper';
import { useGameCategories } from '../hooks/useGameCategories';
import { formatBalanceLocale } from '../utils/numberFormat';





const Sidebar = ({
    isOpen,
    onClose,
    sidebarMode = 'full', // 'full' | 'icons'
    setSidebarMode
}) => {
    const { openPaymentModal, profile, openProfileModal, getCurrentBonusBalance, selectedCurrency, cashbackData, bonusCashbackData, openGlobalCurrencyDropdown, openAccountHistoryModal, openGameHistoryModal } = usePayment();
    const { isAuth } = useAuth();
    const { openGlobalLangDropdown } = useGeo();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const langDropdownRef = useRef(null);
    const [showIosModal, setShowIosModal] = useState(false);
    const [canInstall, setCanInstall] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showIosInstallModal, setShowIosInstallModal] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstall(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then(() => {
                setDeferredPrompt(null);
                setCanInstall(false);
            });
        }
    };

    // Функция-обёртка для onClick, чтобы закрывать сайдбар на мобильных устройствах
    const handleAndClose = (fn) => (...args) => {
        if (typeof fn === 'function') {
            fn(...args);
            // Закрываем сайдбар на мобильных устройствах для всех действий
            if (isMobile && typeof onClose === 'function') {
                onClose();
            }
        }
    };

    const handleAuthModalClose = () => {
        setShowAuthModal(false);
    };

    const handleLoginSuccess = () => {
        // Обновляем страницу после успешной авторизации
        window.location.reload();
    };

    // Функция для навигации к подборке
    const navigateToCollection = (collectionType) => {
        // Переходим на главную страницу с параметром подборки
        navigate(`/?collection=${collectionType}`);
        if (typeof onClose === 'function') onClose();
    };

    // Синхронизируем с логикой Downbar - используем тот же breakpoint
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : true
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    const getFlagFile = (lang) => {
        switch (lang) {
            case "ru": return "/flags/ru.svg";
            case "en": return "/flags/en.svg";
            case "pt": return "/flags/pt.svg";
            case "am": return "/flags/am.svg";
            case "be": return "/flags/be.svg";
            case "tr": return "/flags/tr.svg";
            case "kz": return "/flags/kz.svg";
            case "az": return "/flags/az.svg";
            case "eu": return "/flags/eu.svg";
            default: return "/flags/en.svg";
        }
    };

    // Форматирование таймера кешбека
    function formatTimerFromSeconds(seconds) {
        if (typeof seconds !== 'number' || isNaN(seconds)) return '-';
        const days = Math.floor(seconds / (60 * 60 * 24));
        const hours = Math.floor((seconds / (60 * 60)) % 24);
        const minutes = Math.floor((seconds / 60) % 60);
        const sec = seconds % 60;
        return `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}` +
            `:${sec.toString().padStart(2, '0')}`;
    }

    // Состояние для раскрытых категорий
    const [expandedCategories, setExpandedCategories] = useState({});

    // Получаем категории из бэкенда
    const { categories: gameCategories, iconModeCategories, loading: categoriesLoading } = useGameCategories('sidebar');

    // Функция для рендера категории с переводами
    const renderCategory = (category, level = 0) => {
        const title = t(category.titleKey, category.title || category.key);
        const icon = getIcon(category.icon);

        return {
            ...category,
            title,
            icon,
            level
        };
    };

    // Получаем категории для режима иконок
    const iconModeCollections = iconModeCategories.map(cat => renderCategory(cat));

    // Функция для переключения раскрытия категории
    const toggleCategory = (categoryKey) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    // Если мобильный и сайдбар закрыт, не рендерим его вообще
    if (!isOpen && isMobile) return null;

    return (
        <>
            {showIosModal && <IosInstallModal onClose={() => setShowIosModal(false)} />}
            {showAuthModal && (
                <AuthModal
                    onClose={handleAuthModalClose}
                    isLoginType={true}
                    onLoginSuccess={handleLoginSuccess}
                />
            )}
            {showIosInstallModal && (
                <IosInstallModal onClose={() => setShowIosInstallModal(false)} />
            )}
            {isOpen && isMobile && <div className="sidebar-overlay" onClick={onClose} />}
            <div className={`sidebar${isOpen ? ' open' : ''} ${sidebarMode === 'icons' ? ' sidebar-icons' : ''}`}>

                <div className="sidebar-content">
                    {sidebarMode === 'full' && (
                        <>
                            {isAuth && (
                                <>
                                    <SidebarBalanceBlock />

                                    <div className="sidebar-balance-actions">
                                        <button className="btn withdraw-btn" style={{ width: '100%' }} onClick={handleAndClose(() => openPaymentModal({ type: "withdraw" }))}>
                                            {t("sidebar.withdraw")}
                                        </button>
                                        <button className="btn sign-up glow-blue" style={{ width: '100%' }} onClick={handleAndClose(() => openPaymentModal({ type: "deposit" }))}>
                                            {t("sidebar.deposit")}
                                        </button>
                                    </div>

                                    {/* Кнопка Халява */}
                                    <div className="sidebar-freemoney-block" onClick={handleAndClose(() => navigate('/freemoney'))}>
                                        <span className="sidebar-freemoney-text">{t('sidebar.freebies')}</span>
                                        <img
                                            src="/assets/sidebar/halyavaIcon.png"
                                            alt={t('sidebar.freebies')}
                                            className="sidebar-freemoney-icon"
                                        />
                                    </div>

                                    <div className="profile-section" onClick={handleAndClose(() => navigate('/profile'))}>
                                        <div className="profile-icon">
                                            <FaUser />
                                        </div>
                                        <div className="profile-info">
                                            <p className="username">{profile.username}</p>
                                            <p className="user-id">ID: {profile.MyuserId}</p>
                                        </div>
                                    </div>

                                    {/* Кнопка перехода на страницу бонусов */}
                                    <div className="sidebar-bonuses-button" onClick={handleAndClose(() => navigate('/bonuses'))}>
                                        <div className="sidebar-bonuses-icon">
                                            <FaGift />
                                        </div>
                                        <div className="sidebar-bonuses-text">
                                            {t('sidebar.bonuses', 'Бонусы')}
                                        </div>
                                    </div>






                                </>
                            )}

                            {!isAuth && (
                                <>
                                    <div className="sidebar-balance-actions">
                                        <button className="btn sign-up glow-blue" style={{ width: '100%' }} onClick={handleAndClose(() => setShowAuthModal(true))}>
                                            <FaSignInAlt style={{ marginRight: '8px' }} />
                                            {t("auth.login", "Вход")}
                                        </button>
                                    </div>

                                    {/* Кнопка Халява для неавторизованных */}
                                    <div className="sidebar-freemoney-block" onClick={handleAndClose(() => navigate('/freemoney'))}>
                                        <span className="sidebar-freemoney-text">{t('sidebar.freebies')}</span>
                                        <img
                                            src="/assets/sidebar/halyavaIcon.png"
                                            alt={t('sidebar.freebies')}
                                            className="sidebar-freemoney-icon"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="menu-sections">
                                {/* Категории игр с подкатегориями */}
                                {Object.entries(gameCategories).map(([categoryKey, category]) => {
                                    const renderedCategory = renderCategory(category);

                                    return (
                                        <React.Fragment key={categoryKey}>
                                            <div
                                                className="profile-section"
                                                onClick={() => {
                                                    if (category.isMain) {
                                                        handleAndClose(() => navigate('/'));
                                                    }
                                                    toggleCategory(categoryKey);
                                                }}
                                            >
                                                <div className="profile-icon">{renderedCategory.icon}</div>
                                                <div className="profile-info">
                                                    <p className="username">{renderedCategory.title}</p>
                                                </div>
                                                <div className={`menu-chevron ${expandedCategories[categoryKey] ? 'expanded' : ''}`}>
                                                    <FaChevronDown />
                                                </div>
                                            </div>

                                            {/* Подкатегории */}
                                            {expandedCategories[categoryKey] && category.subcategories && (
                                                <>
                                                    {Object.entries(category.subcategories).map(([subKey, subcategory]) => {
                                                        const renderedSub = renderCategory(subcategory, 1);
                                                        const hasSubcategories = subcategory.subcategories && Object.keys(subcategory.subcategories).length > 0;

                                                        return (
                                                            <React.Fragment key={subKey}>
                                                                <div
                                                                    className="profile-section subcategory-section"
                                                                    onClick={() => {
                                                                        if (hasSubcategories) {
                                                                            toggleCategory(`${categoryKey}.${subKey}`);
                                                                        } else {
                                                                            navigateToCollection(subcategory.key);
                                                                            if (isMobile && typeof onClose === 'function') onClose();
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="profile-icon subcategory-icon">{renderedSub.icon}</div>
                                                                    <div className="profile-info">
                                                                        <p className="username">{renderedSub.title}</p>
                                                                    </div>
                                                                    {hasSubcategories && (
                                                                        <div className={`menu-chevron ${expandedCategories[`${categoryKey}.${subKey}`] ? 'expanded' : ''}`}>
                                                                            <FaChevronDown />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Подподкатегории */}
                                                                {expandedCategories[`${categoryKey}.${subKey}`] && subcategory.subcategories && (
                                                                    <>
                                                                        {Object.entries(subcategory.subcategories).map(([subSubKey, subSubcategory]) => {
                                                                            const renderedSubSub = renderCategory(subSubcategory, 2);

                                                                            return (
                                                                                <div
                                                                                    key={subSubKey}
                                                                                    className="profile-section sub-subcategory-section"
                                                                                    onClick={() => {
                                                                                        navigateToCollection(subSubcategory.key);
                                                                                        if (isMobile && typeof onClose === 'function') onClose();
                                                                                    }}
                                                                                >
                                                                                    <div className="profile-icon sub-subcategory-icon">{renderedSubSub.icon}</div>
                                                                                    <div className="profile-info">
                                                                                        <p className="username">{renderedSubSub.title}</p>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {/* Дополнительные секции для авторизованных пользователей */}
                            {isAuth && (
                                <>
                                    <div className="profile-section" onClick={handleAndClose(() => openAccountHistoryModal())}>
                                        <div className="profile-icon">
                                            <FaHistory />
                                        </div>
                                        <div className="profile-info">
                                            <p className="username">{t('История счёта')}</p>
                                        </div>
                                    </div>

                                    <div className="profile-section" onClick={handleAndClose(() => openGameHistoryModal())}>
                                        <div className="profile-icon">
                                            <FaGamepad />
                                        </div>
                                        <div className="profile-info">
                                            <p className="username">{t('История игр')}</p>
                                        </div>
                                    </div>

                                    <div className="profile-section" onClick={handleAndClose(() => navigate('/referral'))}>
                                        <div className="profile-icon">
                                            <FaUserFriends />
                                        </div>
                                        <div className="profile-info">
                                            <p className="username">{t('Партнёрам')}</p>
                                        </div>
                                    </div>
                                </>
                            )}

                        </>
                    )}



                    {sidebarMode === 'icons' && (
                        <div className="sidebar-icons-list">
                            <div className="sidebar-icons-main">
                                {isAuth ? (
                                    <>
                                        <button className="sidebar-icon-btn glow-blue" title="Пополнить" onClick={handleAndClose(() => openPaymentModal({ type: 'deposit' }))}>
                                            <span style={{ fontSize: '22px', fontWeight: 'bold', marginLeft: 2 }}>+</span>
                                        </button>
                                        <button className="sidebar-icon-btn" title="Профиль" onClick={handleAndClose(() => navigate('/profile'))}><FaUser /></button>
                                    </>
                                ) : (
                                    <button className="sidebar-icon-btn glow-blue" title="Вход" onClick={handleAndClose(() => setShowAuthModal(true))}>
                                        <FaSignInAlt />
                                    </button>
                                )}

                                <button className="sidebar-icon-btn" title="Казино" onClick={handleAndClose(() => navigate('/'))}><FaDice /></button>
                                {iconModeCollections.map((collection) => (
                                    <button
                                        key={collection.key}
                                        className="sidebar-icon-btn"
                                        title={collection.title}
                                        onClick={() => {
                                            navigateToCollection(collection.key);
                                            if (isMobile && typeof onClose === 'function') onClose();
                                        }}
                                    >
                                        {collection.icon}
                                    </button>
                                ))}
                                <button className="sidebar-icon-btn" title="Бонусы" onClick={handleAndClose(() => navigate('/bonuses'))}><FaGift /></button>
                            </div>
                            <div className="sidebar-icons-bottom">
                                <button
                                    className="sidebar-icon-btn sidebar-lang-icon-btn"
                                    title="Язык"
                                    onClick={(e) => {
                                        if (sidebarMode === 'icons' && typeof setSidebarMode === 'function') {
                                            setSidebarMode('full');
                                        } else {
                                            openGlobalLangDropdown(e.currentTarget);
                                        }
                                    }}
                                    ref={langDropdownRef}
                                >
                                    <img src={getFlagFile(i18n.language)} alt="language" className="lang-flag" />
                                </button>
                                <div className="sidebar-slider-controls">
                                    <button className="sidebar-icon-btn sidebar-expand-btn" onClick={() => setSidebarMode('full')} title="Открыть меню"><FaChevronRight /></button>
                                </div>

                            </div>
                        </div>
                    )}
                </div>
                {sidebarMode === 'full' && (
                    <>





                        <div className="sidebar-support-block">
                            <div className="sidebar-support-title-row">
                                <span className="sidebar-support-title">{t('sidebar.support')}</span>
                                <span className="sidebar-support-badge">{t('sidebar.support24_7')}</span>
                            </div>
                            <div className="sidebar-support-actions">
                                <a href="https://t.me/oncaspins" target="_blank" rel="noopener noreferrer" className="sidebar-support-btn">
                                    <FaTelegram />
                                </a>
                            </div>
                        </div>


                        {/* Блок установки приложения */}
                        <div className="sidebar-app-install-block">
                            <div className="sidebar-app-install-title">
                                {t("sidebar.installApp", "Установить приложение")}
                            </div>
                            <div className="sidebar-app-install-description">
                                {t("sidebar.installAppDescription", "Получите 30 фриспинов за установку приложения на Android и привязку Telegram")}
                            </div>
                            <div className="sidebar-app-install-buttons">
                                <button
                                    className="sidebar-app-install-btn"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, width: 48, borderRadius: 5 }}
                                    onClick={() => {
                                        if (canInstall) {
                                            handleInstallClick();
                                        } else {
                                            alert('Для установки приложения откройте сайт через Chrome или другой поддерживаемый браузер на Android или ПК.');
                                        }
                                    }}
                                    title="Установить на устройство"
                                >
                                    <FaWindows color="#fff" />
                                </button>
                                <button
                                    className="sidebar-app-install-btn"
                                    onClick={() => {
                                        setShowIosInstallModal(true);
                                        if (isMobile && typeof onClose === 'function') onClose();
                                    }}
                                    title="iOS"
                                >
                                    <FaApple />
                                </button>
                                <button
                                    className="sidebar-app-install-btn"
                                    onClick={() => {
                                        // Скачивание APK файла
                                        const link = document.createElement('a');
                                        link.href = '/apps/oncaspin.apk';
                                        link.download = 'oncaspin.apk';
                                        link.click();
                                    }}
                                    title="Android"
                                >
                                    <FaAndroid />
                                </button>
                                <button
                                    className="sidebar-app-install-btn"
                                    onClick={() => {
                                        // Логика для Telegram
                                        console.log('Telegram app install');
                                    }}
                                    title="Telegram"
                                >
                                    <FaTelegram />
                                </button>
                            </div>
                        </div>


                        <div className="sidebar-lang-block">
                            <button className="sidebar-icon-btn sidebar-expand-btn" title="Закрыть сайдбар" onClick={onClose}>
                                <FaChevronLeft />
                            </button>
                            <button
                                className="sidebar-lang-switch"
                                onClick={(e) => openGlobalLangDropdown(e.currentTarget)}
                            >
                                <img src={getFlagFile(i18n.language)} alt="language" className="lang-flag" />
                                <span className="sidebar-lang-label">{languageOptions.find(l => l.code === i18n.language)?.label}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export const currencyOptions = [
    { code: "USD", label: "USD ($)", flag: "/flags/us.svg" },
    { code: "RUB", label: "RUB (₽)", flag: "/flags/ru.svg" },
    { code: "BRL", label: "BRL (R$)", flag: "/flags/br.svg" },
    { code: "EUR", label: "EUR (€)", flag: "/flags/eu.svg" },
];

export const SidebarBalanceBlock = () => {
    const { selectedCurrency, getCurrentBalance, openGlobalCurrencyDropdown } = usePayment();
    const { t } = useTranslation();

    // Символ валюты
    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            case 'EUR': return '€';
            default: return '';
        }
    };

    return (
        <div className="sidebar-balance-block">
            <div className="sidebar-balance-stripe" />
            <div className="sidebar-balance-content">
                <div className="sidebar-balance-content-block">
                    <span className="sidebar-balance-label">{t("sidebar.yourBalance")}</span>

                    <div className="sidebar-balance-value">
                        {formatBalanceLocale(getCurrentBalance())} <span className="sidebar-balance-currency-symbol">{getCurrencySymbol(selectedCurrency)}</span>
                    </div>
                </div>
                <div className="sidebar-balance-dropdown-wrapper-wrapper">
                    <div className="sidebar-balance-dropdown-wrapper">
                        <div
                            className="sidebar-balance-dropdown-selected"
                            onClick={(e) => openGlobalCurrencyDropdown(e.currentTarget)}
                        >
                            <span className="sidebar-balance-currency">{selectedCurrency}</span>
                            <img
                                src={currencyOptions.find((c) => c.code === selectedCurrency)?.flag}
                                alt={selectedCurrency}
                                className="sidebar-balance-flag"
                            />

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const languageOptions = [
    { code: "ru", label: "Русский", flag: "/flags/ru.svg" },
    { code: "en", label: "English", flag: "/flags/en.svg" },
    { code: "pt", label: "Português", flag: "/flags/pt.svg" },
    { code: "am", label: "Armenian", flag: "/flags/am.svg" },
    { code: "be", label: "Беларускі", flag: "/flags/be.svg" },
    { code: "tr", label: "Türkçe", flag: "/flags/tr.svg" },
    { code: "kz", label: "Казақша", flag: "/flags/kz.svg" },
    { code: "az", label: "Azərbaycan", flag: "/flags/az.svg" },

];

export default Sidebar; 